// Bundle ladder: 1 mask, 2 masks ($6.49 off = $47.49, ~12%, "Ride with a buddy"), 3 masks
// ($16.00 off = $64.97, ~19.8%, "Squad of three"), 4 masks (25% off = $80.97, "Crew of four"). All are store automatic discounts; the theme
// only sets the quantity. `choice` is the picked tier, `available` the tracked
// stock of the chosen variant (null = untracked or oversell allowed). When the
// stock can't cover the tier, fall back to the largest tier that fits and say
// so honestly. Sold out (<= 0) never claims "only 1 left".
export const TIER_DISCOUNT = { 2: 12, 3: 19, 4: 25 };
export const MAX_TIER = 4;
// Black Friday 2026 (snippets/gm-bf, block sets data-bf): 4 masks 35.17% off = $69.99.
export const BF_TIER4_PERCENT = 35;

// English defaults; the block passes translated templates (locales:
// blockhead.bundle.*) through its <script data-gm-bundle-i18n> blob.
export const BUNDLE_STRINGS = {
  low_stock_one: 'Only {{ count }} left in this {{ noun }}. Mix with {{ others }} to get {{ percent }}% off.',
  low_stock_other: 'Only {{ count }} left in this {{ noun }}. Mix with {{ others }} to get {{ percent }}% off.',
  another_mask: 'another mask',
  other_masks: '{{ count }} other masks',
};

import { toLocal, convertAll, rate, formatMoney } from './gm-money.js';

const fill = (s, vars) => String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (k in vars ? String(vars[k]) : m));

function pluralForm(n, locale) {
  try { return new Intl.PluralRules(locale || 'en').select(n) === 'one' ? 'one' : 'other'; } catch { return n === 1 ? 'one' : 'other'; }
}

export function bundleQuantity(choice, available, { noun = 'colour', strings = {}, locale = 'en', bf = false } = {}) {
  const tier = Number.isFinite(choice) ? Math.min(Math.max(Math.trunc(choice), 1), MAX_TIER) : 1;
  if (tier === 1) return { qty: 1, note: null };
  if (available === null || available === undefined || available >= tier) return { qty: tier, note: null };
  if (available <= 0) return { qty: 1, note: null };
  const t = { ...BUNDLE_STRINGS, ...strings };
  const qty = Math.max(1, Math.min(tier, Math.trunc(available)));
  const missing = tier - qty;
  const others = missing === 1 ? t.another_mask : fill(t.other_masks, { count: missing });
  const template = t[`low_stock_${pluralForm(qty, locale)}`];
  return { qty, note: fill(template, { count: qty, noun, others, percent: bf && tier === 4 ? BF_TIER4_PERCENT : TIER_DISCOUNT[tier] }) };
}

export function availableFor(map, variantId) {
  if (!map || typeof map !== 'object') return null;
  const value = map[variantId];
  return value === null || value === undefined ? null : value;
}

// Clamp to the quantity component's effective max (quantity rules / what is
// left after the cart), never below 1. null/undefined max = no limit.
export function clampQuantity(qty, max) {
  if (max === null || max === undefined || !Number.isFinite(max)) return qty;
  return Math.max(1, Math.min(qty, max));
}

// A document-wide `variant:update` (VariantUpdateEvent, assets/events.js) only
// belongs to this bundle when it carries a variant AND its detail.data.productId
// matches ours: the quick-add modal and other product cards fire the same event.
export function isOwnVariantUpdate(detail, productId) {
  const id = detail?.data?.productId;
  if (!detail?.resource?.id || id === null || id === undefined || !productId) return false;
  return String(id) === String(productId);
}

// True for Meta ad clicks (utm_source facebook/fb/instagram/ig/meta, fbclid)
// and the FB/IG in-app browser. Remembered for the session so later PDP views
// keep the 1-mask default.
export function isPaidSocial() {
  try {
    if (sessionStorage.getItem('gm-paid-social') === '1') return true;
    const p = new URLSearchParams(location.search);
    const src = (p.get('utm_source') || '').toLowerCase();
    const hit = p.has('fbclid') || /^(facebook|fb|instagram|ig|meta)$/.test(src) || /FBAN|FBAV|Instagram/.test(navigator.userAgent);
    if (hit) sessionStorage.setItem('gm-paid-social', '1');
    return hit;
  } catch {
    return false;
  }
}

// "Add all faces" button: data-gm-add-all="id:1,id:1,..." (the cart permalink
// format) → /cart/add.js items. Bad pairs are dropped; empty = let the link work.
export function parseAddAll(spec) {
  return String(spec || '').split(',').map((pair) => {
    const [id, qty] = pair.split(':').map((n) => Number.parseInt(n, 10));
    return { id, quantity: Number.isFinite(qty) && qty > 0 ? qty : 1 };
  }).filter((item) => Number.isFinite(item.id) && item.id > 0);
}

// Per-mask face pickers (one <select> per mask in the chosen tier) → add.js
// items, same faces merged. null when every mask is the same face: then the
// normal add-to-cart (quantity = tier) already does the right thing.
export function mixItems(ids) {
  const counts = new Map();
  for (const raw of ids || []) {
    const id = Number.parseInt(raw, 10);
    if (Number.isFinite(id) && id > 0) counts.set(id, (counts.get(id) || 0) + 1);
  }
  if (counts.size < 2) return null;
  return [...counts].map(([id, quantity]) => ({ id, quantity }));
}

// A face picker counts as "picked" (thumbnail in full colour, no "Pick me!")
// once the shopper changed it; slot 1 also once a face was picked on the page.
export function slotPicked({ slot, touched, facePicked }) {
  return Boolean(touched) || (String(slot) === '1' && Boolean(facePicked));
}

const QUANTITY_INPUT ='product-form-component input[name="quantity"], form[action*="/cart/add"] input[name="quantity"]';

if (typeof customElements !== 'undefined' && !customElements.get('gm-bundle')) {
  customElements.define('gm-bundle', class extends HTMLElement {
    connectedCallback() {
      // Horizon's quick-add modal (snippets/quick-add-modal.liquid) morphs the
      // PDP's .product-details, this block included, into a <dialog> outside
      // any section. Stay inert there: never touch the page's quantity.
      if (this.closest('.quick-add-modal, dialog, quick-add-dialog')) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      const inventoryScript = this.querySelector('script[data-gm-inventory]');
      this.inventoryMap = inventoryScript ? JSON.parse(inventoryScript.textContent) : {};
      try { this.strings = JSON.parse(this.querySelector('script[data-gm-bundle-i18n]')?.textContent || '{}'); } catch { this.strings = {}; }
      // The default (the data-default radio) is checked here rather than with a `checked` attribute:
      // a checked radio inserted by the quick-add morph would otherwise uncheck
      // this group on the main page (same template = same block id = same name).
      // Since 2026-10-19 every visitor starts on 1 mask (the 4-pack default ran Oct 18-19).
      if (!this.querySelector('input[type="radio"]:checked')) {
        const def = this.querySelector('input[data-default]');
        if (def) def.checked = true;
      }
      this.localizeShipping();
      this.querySelector('[data-gm-add-all]')?.addEventListener('click', (e) => this.addAll(e), { signal });
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-gm-slot]')) e.target.dataset.touched = '1';
        this.apply();
      }, { signal });
      document.addEventListener('variant:update', (e) => {
        if (!isOwnVariantUpdate(e.detail, this.dataset.productId)) return;
        this.dataset.variantId = e.detail.resource.id;
        this.dataset.facePicked = '1';
        const first = this.querySelector('[data-gm-slot]');
        if (first) { first.value = String(e.detail.resource.id); delete first.dataset.touched; }
        queueMicrotask(() => this.apply());
      }, { signal });
      this.apply();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
    }
    // The Liquid compares tier totals with the USD free-shipping threshold. Outside USD the
    // threshold converts at the market rate while prices are rounded, so redo it here.
    localizeShipping() {
      convertAll(this);
      // Tiers 2/3 are fixed USD discounts ($6.49 / $16.00). Liquid can't see the market rate, so outside USD
      // swap its estimate for what checkout charges: discount = USD amount x rate, total = full price - discount.
      if (rate() !== 1) {
        const sample = this.dataset.gmFormat;
        this.querySelectorAll('[data-gm-off-usd]').forEach((el) => {
          const n = Number(el.dataset.gmN);
          const full = Number(el.dataset.gmFull);
          const oldTotal = Number(el.dataset.gmTierTotal);
          const off = toLocal(el.dataset.gmOffUsd);
          const total = full - off;
          const swaps = [[oldTotal, total], [full - oldTotal, off], [Math.round(oldTotal / n), Math.round(total / n)]]
            .map(([a, b]) => [formatMoney(a, sample), formatMoney(b, sample)]);
          const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
          for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            let t = node.nodeValue;
            for (const [a, b] of swaps) t = t.split(a).join(b);
            node.nodeValue = t;
          }
          el.dataset.gmTierTotal = String(total);
        });
      }
      const free = toLocal(this.dataset.freeUsd || 4000);
      this.querySelectorAll('[data-gm-tier-total]').forEach((el) => {
        const isFree = Number(el.dataset.gmTierTotal) >= free;
        el.querySelector('[data-free]').hidden = !isFree;
        el.querySelector('[data-nofree]').hidden = isFree;
      });
    }
    // One of every face into the cart, then the cart page (the automatic
    // tier discount shows there). Any failure falls back to the permalink.
    async addAll(e) {
      const link = e.currentTarget;
      const items = parseAddAll(link.dataset.gmAddAll);
      if (!items.length) return;
      e.preventDefault();
      link.setAttribute('aria-busy', 'true');
      try {
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error(String(res.status));
        location.href = link.dataset.cartUrl || `${root}cart`;
      } catch {
        location.href = link.href;
      }
    }
    // Called by the TikTok landing's variant radios (assets/gm-landing-hero.js).
    setVariant(variantId) {
      this.dataset.variantId = String(variantId);
      this.apply();
    }
    apply() {
      if (!this.abort) return;
      const scope = this.closest('[data-product-grid-content], .shopify-section');
      if (!scope) return;
      const input = scope.querySelector(QUANTITY_INPUT) || scope.querySelector('input[name="quantity"]');
      const choice = Number(this.querySelector('input[type="radio"]:checked')?.value || 1);
      const available = availableFor(this.inventoryMap, this.dataset.variantId);
      const { qty, note } = bundleQuantity(choice, available, {
        noun: this.dataset.noun || 'colour',
        strings: this.strings,
        locale: document.documentElement.lang || 'en',
        bf: 'bf' in this.dataset,
      });
      if (input) this.setQuantity(input, qty);
      const n = this.querySelector('[data-note]'); if (n) { n.textContent = note || ''; n.hidden = !note; }
      this.syncSlots(choice);
      this.syncForm(scope);
    }
    // Show one face picker per mask for 2+ tiers. Untouched pickers follow
    // the face picked on the page, so the default is still "N of this face".
    syncSlots(choice) {
      const pick = this.querySelector('[data-gm-pick]');
      if (!pick) return;
      pick.hidden = choice < 2;
      pick.querySelectorAll('[data-slot]').forEach((row) => {
        row.hidden = Number(row.dataset.slot) > choice;
        const sel = row.querySelector('[data-gm-slot]');
        if (sel && !sel.dataset.touched && this.dataset.variantId) sel.value = String(this.dataset.variantId);
        if (sel) this.syncThumb(row, sel);
      });
    }
    // Show the picked face's photo next to its picker; flip it in when the
    // shopper picks a (new) face.
    syncThumb(row, sel) {
      const thumb = row.querySelector('[data-gm-thumb]');
      if (!thumb) return;
      let changed = false;
      thumb.querySelectorAll('img[data-face]').forEach((img) => {
        const show = img.dataset.face === sel.value;
        if (show && img.hidden) changed = true;
        img.hidden = !show;
      });
      const picked = slotPicked({ slot: row.dataset.slot, touched: sel.dataset.touched, facePicked: this.dataset.facePicked });
      const wasPicked = 'picked' in row.dataset;
      row.toggleAttribute('data-picked', picked);
      if (picked && (changed || !wasPicked)) {
        thumb.classList.remove('is-flip');
        void thumb.offsetWidth;
        thumb.classList.add('is-flip');
      }
    }
    slotIds() {
      const pick = this.querySelector('[data-gm-pick]');
      if (!pick || pick.hidden) return [];
      return [...pick.querySelectorAll('[data-slot]:not([hidden]) [data-gm-slot]')].map((s) => s.value);
    }
    // Mixed faces: rewrite the product form itself rather than intercepting
    // the add-to-cart (Horizon's handler and a store app both grab the
    // submit). The id/quantity inputs are disabled (left out of FormData) and
    // hidden items[n][id]/items[n][quantity] inputs added; /cart/add accepts
    // that, so Horizon's own submit adds one line per face.
    syncForm(scope) {
      const form = scope?.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      form.querySelectorAll('[data-gm-mix]').forEach((el) => el.remove());
      const items = mixItems(this.slotIds());
      const plain = [...form.elements].filter((el) => el.name === 'id' || el.name === 'quantity' || el.dataset.gmMixOff);
      plain.forEach((el) => {
        if (items) { el.dataset.gmMixOff = '1'; el.disabled = true; } else if (el.dataset.gmMixOff) { delete el.dataset.gmMixOff; el.disabled = false; }
      });
      // Express buttons (Shop Pay / Apple Pay / PayPal) read only the page
      // variant x quantity and skip the items[] inputs, so a mixed bundle
      // would check out as N of one face. Hide them while faces are mixed;
      // the cart page still offers them.
      // A CSS rule on the section also covers buttons Shopify injects later
      // (they load lazily when scrolled into view).
      if (!document.getElementById('gm-mix-express')) {
        const style = document.createElement('style');
        style.id = 'gm-mix-express';
        style.textContent = '[data-gm-mixed] .shopify-payment-button,[data-gm-mixed] shopify-accelerated-checkout,[data-gm-mixed] more-payment-options-link{display:none!important}';
        document.head.append(style);
      }
      scope.toggleAttribute('data-gm-mixed', !!items);
      if (!items) return;
      items.forEach((item, i) => {
        for (const [key, value] of [['id', item.id], ['quantity', item.quantity]]) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `items[${i}][${key}]`;
          input.value = String(value);
          input.dataset.gmMix = '';
          form.append(input);
        }
      });
    }
    // Drive Horizon's quantity component (assets/component-quantity-selector.js)
    // through its own update path, so minus/plus states, the max and the sticky
    // ATC's "×2" (QuantitySelectorUpdateEvent) all follow the bundle choice.
    setQuantity(input, qty) {
      const qs = input.closest('quantity-selector-component');
      if (qs && !customElements.get('quantity-selector-component')) {
        customElements.whenDefined('quantity-selector-component').then(() => this.apply());
        return;
      }
      const ready = !!qs && typeof qs.updateButtonStates === 'function';
      const value = ready ? clampQuantity(qty, qs.getEffectiveMax()) : qty;
      input.value = String(value);
      if (ready) {
        qs.updateButtonStates();
        qs.onQuantityChange();
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}
